<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import Button from 'dashboard/components-next/button/Button.vue';
import { useBillingOverview } from '../composables/useBillingOverview';
import { ORDER_TYPES } from '../api';
import { formatUsdFromCents, formatCredits } from '../helpers/money';
import { evaluatePurchaseGate } from '../helpers/purchaseGuards';
import PlanCard from './PlanCard.vue';
import WalletCard from './WalletCard.vue';
import CreditsCard from './CreditsCard.vue';
import SeatsCard from './SeatsCard.vue';
import OrdersTable from './OrdersTable.vue';
import InvoicesTable from './InvoicesTable.vue';
import PurchaseConfirmDialog from './PurchaseConfirmDialog.vue';

const { t } = useI18n();
const store = useStore();

const accountId = computed(() => store.state.route?.params?.accountId);
const isAdmin = computed(
  () => store.getters.getCurrentRole === 'administrator'
);

const {
  loading,
  error,
  overview,
  subscription,
  seats,
  wallet,
  credits,
  catalog,
  isPro,
  balanceCents,
  spendFrozen,
  ledgerLoading,
  ledgerError,
  ledger,
  ordersLoading,
  ordersError,
  orders,
  invoicesLoading,
  invoicesError,
  invoices,
  submitting,
  topupSubmitting,
  refreshAll,
  loadLedger,
  purchase,
  startTopup,
} = useBillingOverview(accountId);

const showLedger = ref(false);
const purchaseError = ref('');
const topupError = ref('');
const topupNotice = ref('');

const confirm = reactive({
  open: false,
  title: '',
  summaryLines: [],
  amountCents: 0,
  payload: null,
  requiresPro: false,
  targetSeatLimit: null,
});

const resolveErrorMessage = code => {
  if (!code) return '';
  const key = `CUSTOM.BILLING.ERRORS.${String(code).toUpperCase()}`;
  const translated = t(key);
  if (translated !== key) return translated;
  if (code === 'forbidden') return t('CUSTOM.COMMON.ADMIN_ONLY');
  if (code === 'network_error') return t('CUSTOM.BILLING.ERRORS.NETWORK');
  return t('CUSTOM.BILLING.ERRORS.GENERIC');
};

const pageError = computed(() => resolveErrorMessage(error.value));

const openConfirm = ({
  title,
  summaryLines,
  amountCents,
  payload,
  requiresPro = false,
  targetSeatLimit = null,
}) => {
  confirm.title = title;
  confirm.summaryLines = summaryLines;
  confirm.amountCents = amountCents;
  confirm.payload = payload;
  confirm.requiresPro = requiresPro;
  confirm.targetSeatLimit = targetSeatLimit;
  confirm.open = true;
};

const closeConfirm = () => {
  confirm.open = false;
  confirm.payload = null;
};

const onUpgrade = () => {
  const amount = Number(catalog.value?.pro_price_cents) || 2000;
  openConfirm({
    title: t('CUSTOM.BILLING.PLAN.UPGRADE'),
    amountCents: amount,
    summaryLines: [
      {
        label: t('CUSTOM.BILLING.CONFIRM.ITEM'),
        value: t('CUSTOM.BILLING.PLAN.PRO'),
      },
      {
        label: t('CUSTOM.BILLING.CONFIRM.CYCLE'),
        value: t('CUSTOM.BILLING.CONFIRM.DAYS_30'),
      },
    ],
    payload: { order_type: ORDER_TYPES.PLAN_UPGRADE },
  });
};

const onRenew = () => {
  const amount = Number(catalog.value?.pro_price_cents) || 2000;
  openConfirm({
    title: t('CUSTOM.BILLING.PLAN.RENEW'),
    amountCents: amount,
    summaryLines: [
      {
        label: t('CUSTOM.BILLING.CONFIRM.ITEM'),
        value: t('CUSTOM.BILLING.PLAN.PRO'),
      },
      {
        label: t('CUSTOM.BILLING.CONFIRM.CYCLE'),
        value: t('CUSTOM.BILLING.CONFIRM.DAYS_30'),
      },
    ],
    payload: { order_type: ORDER_TYPES.PLAN_RENEW },
  });
};

const onBuyCredits = draft => {
  openConfirm({
    title: t('CUSTOM.BILLING.CREDITS.BUY'),
    amountCents: draft.amount_cents,
    requiresPro: true,
    summaryLines: [
      {
        label: t('CUSTOM.BILLING.CONFIRM.ITEM'),
        value: t('CUSTOM.BILLING.CREDITS.PACK_SUMMARY', {
          credits: formatCredits(draft.credits),
          packs: draft.pack_count,
        }),
      },
    ],
    payload: {
      order_type: ORDER_TYPES.AI_CREDITS,
      pack_count: draft.pack_count,
    },
  });
};

const onChangeSeats = draft => {
  openConfirm({
    title: t('CUSTOM.BILLING.SEATS.APPLY'),
    amountCents: draft.amount_cents,
    requiresPro: true,
    targetSeatLimit: draft.seat_limit,
    summaryLines: [
      {
        label: t('CUSTOM.BILLING.CONFIRM.ITEM'),
        value: t('CUSTOM.BILLING.SEATS.SUMMARY', {
          limit: draft.seat_limit,
          delta: draft.delta,
        }),
      },
      {
        label: t('CUSTOM.BILLING.CONFIRM.AMOUNT'),
        value:
          draft.delta > 0
            ? formatUsdFromCents(draft.amount_cents)
            : t('CUSTOM.BILLING.SEATS.NO_CHARGE'),
      },
    ],
    payload: {
      order_type: ORDER_TYPES.SEATS,
      seat_limit: draft.seat_limit,
    },
  });
};

const onConfirmPurchase = async () => {
  if (!confirm.payload) return;
  const gate = evaluatePurchaseGate({
    amountCents: confirm.amountCents,
    balanceCents: balanceCents.value,
    submitting: submitting.value,
    requiresPro: confirm.requiresPro,
    isPro: isPro.value,
    targetSeatLimit: confirm.targetSeatLimit,
    seatsUsed: seats.value?.used,
    seatCap: catalog.value?.seat_cap,
    walletSpendFrozen: spendFrozen.value,
  });
  if (!gate.ok) {
    purchaseError.value = resolveErrorMessage(gate.reason);
    return;
  }

  purchaseError.value = '';
  const result = await purchase(confirm.payload);
  if (result.ok) {
    closeConfirm();
    return;
  }
  purchaseError.value = resolveErrorMessage(result.reason);
};

const onTopup = async amountCents => {
  topupError.value = '';
  topupNotice.value = '';
  const result = await startTopup(amountCents);
  if (result.redirecting) return;
  if (!result.ok) {
    topupError.value = resolveErrorMessage(result.reason);
  }
};

const toggleLedger = async () => {
  showLedger.value = !showLedger.value;
  if (showLedger.value && !ledger.value.length) {
    await loadLedger();
  }
};

const readTopupReturnState = () => {
  const params = new URLSearchParams(window.location.search || '');
  const flag = params.get('cash_topup');
  if (flag === 'success') {
    topupNotice.value = t('CUSTOM.BILLING.WALLET.TOPUP_RETURN_SUCCESS');
  } else if (flag === 'cancel') {
    topupNotice.value = t('CUSTOM.BILLING.WALLET.TOPUP_RETURN_CANCEL');
  }
  if (flag && window.history?.replaceState) {
    const url = new URL(window.location.href);
    url.searchParams.delete('cash_topup');
    url.searchParams.delete('invoice');
    window.history.replaceState({}, '', url.pathname + url.search + url.hash);
  }
};

onMounted(async () => {
  if (!isAdmin.value) return;
  readTopupReturnState();
  await refreshAll();
});
</script>

<template>
  <div class="h-full w-full overflow-y-auto">
    <div class="p-6 pb-16 max-w-6xl mx-auto space-y-5">
      <header class="space-y-1">
        <h1 class="text-xl font-semibold text-n-slate-12 mb-0">
          {{ t('CUSTOM.BILLING.PAGE.TITLE') }}
        </h1>
        <p class="text-sm text-n-slate-10 mb-0">
          {{ t('CUSTOM.BILLING.PAGE.SUBTITLE') }}
        </p>
      </header>

      <div v-if="!isAdmin" class="text-n-ruby-9 text-sm" role="alert">
        {{ t('CUSTOM.COMMON.ADMIN_ONLY') }}
      </div>

      <template v-else>
        <div v-if="loading && !overview" class="text-sm text-n-slate-11">
          {{ t('CUSTOM.COMMON.LOADING') }}
        </div>

        <div
          v-else-if="pageError && !overview"
          class="rounded-lg border border-n-weak bg-n-solid-2 p-4 space-y-3"
          role="alert"
        >
          <p class="mb-0 text-sm text-n-ruby-9">{{ pageError }}</p>
          <Button
            size="sm"
            color="slate"
            variant="outline"
            :label="t('CUSTOM.BILLING.PAGE.RETRY')"
            @click="refreshAll()"
          />
        </div>

        <template v-else-if="overview">
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <PlanCard
              :subscription="subscription"
              :seats="seats"
              :catalog="catalog"
              :submitting="submitting"
              @upgrade="onUpgrade"
              @renew="onRenew"
            />
            <WalletCard
              :wallet="wallet"
              :ledger="ledger"
              :ledger-loading="ledgerLoading"
              :ledger-error="ledgerError"
              :show-ledger="showLedger"
              :topup-submitting="topupSubmitting"
              :topup-error="topupError"
              :topup-notice="topupNotice"
              @toggle-ledger="toggleLedger"
              @topup="onTopup"
            />
          </div>

          <div class="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <CreditsCard
              :credits="credits"
              :catalog="catalog"
              :is-pro="isPro"
              :submitting="submitting"
              @buy="onBuyCredits"
            />
            <SeatsCard
              :seats="seats"
              :subscription="subscription"
              :catalog="catalog"
              :is-pro="isPro"
              :submitting="submitting"
              @changeSeats="onChangeSeats"
            />
          </div>

          <InvoicesTable
            :account-id="accountId"
            :invoices="invoices"
            :loading="invoicesLoading"
            :error="invoicesError"
          />

          <OrdersTable
            :orders="orders"
            :loading="ordersLoading"
            :error="ordersError"
          />

          <p
            v-if="purchaseError"
            class="mb-0 text-sm text-n-ruby-9"
            role="alert"
          >
            {{ purchaseError }}
          </p>
        </template>

        <PurchaseConfirmDialog
          :open="confirm.open"
          :title="confirm.title"
          :summary-lines="confirm.summaryLines"
          :amount-cents="confirm.amountCents"
          :balance-cents="balanceCents"
          :is-loading="submitting"
          :requires-pro="confirm.requiresPro"
          :is-pro="isPro"
          :target-seat-limit="confirm.targetSeatLimit"
          :seats-used="seats?.used || 0"
          :seat-cap="catalog?.seat_cap"
          :wallet-spend-frozen="spendFrozen"
          @confirm="onConfirmPurchase"
          @close="closeConfirm"
        />
      </template>
    </div>
  </div>
</template>
